/* ═══════════════════════════════════════════════════════════
   Custom Theme Storage & CSS Injection
   Persists user-created styles/colour-schemes to localStorage
   and injects the corresponding CSS variables at runtime.
   ═══════════════════════════════════════════════════════════ */

const LS_KEY_STYLES = 'valpha-custom-styles';
const LS_KEY_COLORS = 'valpha-custom-color-schemes';
const STYLE_TAG_ID  = 'valpha-custom-themes-css';

/* ── Helpers ── */
const read = (key) => {
  try { return JSON.parse(localStorage.getItem(key)) || []; }
  catch { return []; }
};
const write = (key, data) => localStorage.setItem(key, JSON.stringify(data));

const slugify = (str) =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

/* ═══════════════════════════════════════════════════════════
   Colour-scheme CSS variable mapping
   Keys match what the editor form uses → CSS variable names
   ═══════════════════════════════════════════════════════════ */
const COLOR_VAR_MAP = {
  primary:          '--color-primary',
  primaryHover:     '--color-primary-hover',
  secondary:        '--color-secondary',
  secondaryHover:   '--color-secondary-hover',
  accent:           '--color-accent',
  backgroundBody:   '--color-background-body',
  backgroundSurface:'--color-background-surface',
  backgroundInput:  '--color-background-input',
  textPrimary:      '--color-text-primary',
  textSecondary:    '--color-text-secondary',
  textAccent:       '--color-text-accent',
  borderDefault:    '--color-border-default',
  destructive:      '--color-destructive',
  destructiveHover: '--color-destructive-hover',
  success:          '--color-success',
  successHover:     '--color-success-hover',
  warning:          '--color-warning',
  info:             '--color-info',
  sidebar:          '--color-sidebar',
  sidebarText:      '--color-sidebar-text',
};

/* ═══════════════════════════════════════════════════════════
   Layout style CSS variable mapping
   ═══════════════════════════════════════════════════════════ */
const STYLE_VAR_MAP = {
  radiusSm:         '--radius-sm',
  radiusMd:         '--radius-md',
  radiusLg:         '--radius-lg',
  radiusXl:         '--radius-xl',
  radius2xl:        '--radius-2xl',
  fontPrimary:      '--font-primary',
  letterSpacing:    '--letter-spacing',
  textTransformBtn: '--text-transform-btn',
  cardBorderWidth:  '--card-border-width',
  cardPadding:      '--card-padding',
  shadowPreset:     null, // handled separately (maps to sm/md/lg/xl)
};

/* Shadow presets — friendly labels → actual CSS values */
export const SHADOW_PRESETS = {
  subtle: {
    label: 'Subtle',
    sm: '0 1px 2px rgba(0,0,0,0.05)',
    md: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
    lg: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
    xl: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
  },
  material: {
    label: 'Material (MUI)',
    sm: '0 2px 1px -1px rgba(0,0,0,.2), 0 1px 1px rgba(0,0,0,.14), 0 1px 3px rgba(0,0,0,.12)',
    md: '0 3px 3px -2px rgba(0,0,0,.2), 0 3px 4px rgba(0,0,0,.14), 0 1px 8px rgba(0,0,0,.12)',
    lg: '0 5px 5px -3px rgba(0,0,0,.2), 0 8px 10px 1px rgba(0,0,0,.14), 0 3px 14px 2px rgba(0,0,0,.12)',
    xl: '0 8px 10px -5px rgba(0,0,0,.2), 0 16px 24px 2px rgba(0,0,0,.14), 0 6px 30px 5px rgba(0,0,0,.12)',
  },
  bold: {
    label: 'Bold',
    sm: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
    md: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
    lg: '0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)',
    xl: '0 19px 38px rgba(0,0,0,0.30), 0 15px 12px rgba(0,0,0,0.22)',
  },
  none: {
    label: 'Flat (no shadow)',
    sm: 'none',
    md: 'none',
    lg: 'none',
    xl: 'none',
  },
};

/* Font presets */
export const FONT_PRESETS = [
  { id: 'arial',    label: 'Arial',          value: "Arial, sans-serif" },
  { id: 'roboto',   label: 'Roboto',         value: "'Roboto', 'Segoe UI', Arial, sans-serif" },
  { id: 'inter',    label: 'Inter',          value: "'Inter', system-ui, sans-serif" },
  { id: 'system',   label: 'System Default', value: "system-ui, -apple-system, sans-serif" },
  { id: 'mono',     label: 'Monospace',      value: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" },
];

/* Default values for new custom items */
export const DEFAULT_COLOR_SCHEME = {
  primary: '#2563eb', primaryHover: '#1d4ed8',
  secondary: '#93c5fd', secondaryHover: '#60a5fa',
  accent: '#28a745',
  backgroundBody: '#f8fafc', backgroundSurface: '#ffffff', backgroundInput: '#f1f5f9',
  textPrimary: '#1e293b', textSecondary: '#6b7280', textAccent: '#ffffff',
  borderDefault: '#e5e7eb',
  destructive: '#ef4444', destructiveHover: '#b91c1c',
  success: '#22c55e', successHover: '#15803d',
  warning: '#f59e0b', info: '#3b82f6',
  sidebar: '#93c5fd', sidebarText: '#1e293b',
};

export const DEFAULT_LAYOUT_STYLE = {
  radiusSm: '0.25rem', radiusMd: '0.375rem', radiusLg: '0.5rem',
  radiusXl: '0.75rem', radius2xl: '1rem',
  fontPrimary: 'arial',
  letterSpacing: 'normal',
  textTransformBtn: 'none',
  cardBorderWidth: '1px',
  cardPadding: '1.5rem',
  shadowPreset: 'subtle',
};

/* ═══════════════════════════════════════════════════════════
   CRUD: Custom Colour Schemes
   Each stored as: { id, label, icon, isDark, custom:true, colors:{…} }
   ═══════════════════════════════════════════════════════════ */
export function getCustomColorSchemes() { return read(LS_KEY_COLORS); }

export function saveCustomColorScheme({ id, label, icon, isDark, colors }) {
  const list = read(LS_KEY_COLORS);
  const existing = list.findIndex((c) => c.id === id);
  const entry = { id, label, icon: icon || '🎨', isDark: !!isDark, custom: true, colors };
  if (existing >= 0) list[existing] = entry; else list.push(entry);
  write(LS_KEY_COLORS, list);
  injectAllCustomCSS();
  return entry;
}

export function deleteCustomColorScheme(id) {
  write(LS_KEY_COLORS, read(LS_KEY_COLORS).filter((c) => c.id !== id));
  injectAllCustomCSS();
}

/* ═══════════════════════════════════════════════════════════
   CRUD: Custom Layout Styles
   Each stored as: { id, label, icon, description, custom:true, vars:{…} }
   ═══════════════════════════════════════════════════════════ */
export function getCustomStyles() { return read(LS_KEY_STYLES); }

export function saveCustomStyle({ id, label, icon, description, vars }) {
  const list = read(LS_KEY_STYLES);
  const existing = list.findIndex((s) => s.id === id);
  const entry = { id, label, icon: icon || '🎨', description: description || '', custom: true, vars };
  if (existing >= 0) list[existing] = entry; else list.push(entry);
  write(LS_KEY_STYLES, list);
  injectAllCustomCSS();
  return entry;
}

export function deleteCustomStyle(id) {
  write(LS_KEY_STYLES, read(LS_KEY_STYLES).filter((s) => s.id !== id));
  injectAllCustomCSS();
}

/* ═══════════════════════════════════════════════════════════
   Generate a unique ID for a new custom item
   ═══════════════════════════════════════════════════════════ */
export function generateId(label) {
  return `custom-${slugify(label)}-${Date.now().toString(36)}`;
}

/* ═══════════════════════════════════════════════════════════
   CSS Injection — build <style> from all custom items
   ═══════════════════════════════════════════════════════════ */
function buildColorCSS(scheme) {
  const lines = Object.entries(scheme.colors)
    .filter(([key]) => COLOR_VAR_MAP[key])
    .map(([key, val]) => `  ${COLOR_VAR_MAP[key]}: ${val};`);
  return `.colors-${scheme.id} {\n${lines.join('\n')}\n}`;
}

function buildStyleCSS(style) {
  const vars = style.vars || {};
  const lines = [];

  // Radius
  Object.entries(STYLE_VAR_MAP).forEach(([key, cssVar]) => {
    if (cssVar && vars[key] != null) {
      lines.push(`  ${cssVar}: ${vars[key]};`);
    }
  });

  // Font
  if (vars.fontPrimary) {
    const preset = FONT_PRESETS.find((f) => f.id === vars.fontPrimary);
    if (preset) {
      lines.push(`  --font-primary: ${preset.value};`);
      lines.push(`  --font-secondary: ${preset.value};`);
    }
  }

  // Shadows
  if (vars.shadowPreset && SHADOW_PRESETS[vars.shadowPreset]) {
    const s = SHADOW_PRESETS[vars.shadowPreset];
    lines.push(`  --shadow-sm: ${s.sm};`);
    lines.push(`  --shadow-md: ${s.md};`);
    lines.push(`  --shadow-lg: ${s.lg};`);
    lines.push(`  --shadow-xl: ${s.xl};`);
  }

  return `.style-${style.id} {\n${lines.join('\n')}\n}`;
}

export function injectAllCustomCSS() {
  let tag = document.getElementById(STYLE_TAG_ID);
  if (!tag) {
    tag = document.createElement('style');
    tag.id = STYLE_TAG_ID;
    document.head.appendChild(tag);
  }

  const colorSchemes = getCustomColorSchemes();
  const styles = getCustomStyles();

  const css = [
    '/* Custom themes — injected at runtime */',
    ...colorSchemes.map(buildColorCSS),
    ...styles.map(buildStyleCSS),
  ].join('\n\n');

  tag.textContent = css;
}

/* ── Call on app startup to hydrate any saved custom themes ── */
export function initCustomThemes() {
  injectAllCustomCSS();
}
